import { PropsWithChildren, useMemo, useState } from "react";
import { Footer } from "./Footer";
import { Header } from "./Header";
import {
  CollapsibleSection,
  LinkItem,
  NavDrawer,
} from "@/components/page/NavDrawer.tsx";
import { useStore } from "@/stores/useStore.ts";
import {
  CalendarDaysIcon,
  CogIcon,
  HomeIcon,
  PlugZapIcon,
  User2Icon,
  UsersIcon,
  WrenchIcon,
} from "lucide-react";
import { Toaster } from "@/components/base/Toaster.tsx";

export const BasePage = ({ children }: PropsWithChildren) => {
  const authState = useStore((s) => s.authState);
  const tenant = useStore((s) => s.tenant);
  const tenantRole = useStore((s) => s.tenantRole);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const allowedTenants = useStore((s) => s.allowedTenants);

  const handleMenuButtonClick = () => {
    setNavDrawerOpen(!navDrawerOpen);
  };

  const linkItems = useMemo<Array<Array<LinkItem | CollapsibleSection>>>(() => {
    const links = [
      [
        {
          label: "Home",
          icon: <HomeIcon className="h-4 w-4 mr-2" />,
          href: "/",
        },
        {
          label: "Profile",
          icon: <User2Icon className="h-4 w-4 mr-2" />,
          href: "/users/me",
        },
      ],
    ];
    if (tenant) {
      links[0].push({
        label: "Technicians",
        icon: <WrenchIcon className="h-4 w-4 mr-2" />,
        href: `app/${tenant.id}/technicians`,
      });
      links[0].push({
        label: "Generators",
        icon: <PlugZapIcon className="h-4 w-4 mr-2" />,
        href: `app/${tenant.id}/generators`,
      });
      links[0].push({
        label: "Customers",
        icon: <UsersIcon className="h-4 w-4 mr-2" />,
        href: `app/${tenant.id}/customers`,
      });
      links[0].push({
        label: "Appointments",
        icon: <CalendarDaysIcon className="h-4 w-4 mr-2" />,
        href: `app/${tenant.id}/appointments`,
      });
    }
    if (tenant && tenantRole && ["admin", "superAdmin"].includes(tenantRole)) {
      links[0].push({
        label: "Company Settings",
        icon: <CogIcon className="h-4 w-4 mr-2" />,
        href: `app/${tenant.id}/companySettings`,
      });
    }
    return links;
  }, [tenant]);

  return (
    <div className="flex flex-col h-screen overscroll-none">
      <div className="flex-shrink-0">
        {/* Header */}
        <Header onMenuClick={handleMenuButtonClick} />
      </div>

      <div className="flex flex-grow overflow-hidden bg-white relative">
        {/* Main Content and Drawer */}
        {authState !== "unauthenticated" &&
          allowedTenants &&
          allowedTenants.length > 0 &&
          linkItems && (
            <NavDrawer
              linkItems={linkItems}
              isCollapsed={navDrawerOpen}
              setIsCollapsed={setNavDrawerOpen}
            />
          )}

        <main className="flex-1 flex-grow flex flex-col overflow-x-auto overflow-y-auto pb-8">
          {children}
          <Toaster />
        </main>
      </div>

      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
};
